import { EPTSessionType } from '@tableau/microview-communication-service/lib/src/channelDefinitions/SearchEPTChannelDefinitions';
import { SiteUrls } from '@tableau/shared-urls';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Location } from '~commonInfra/utils/Location';
import { Messages } from '~src/codegen/Localize';
import {CollectionUtils} from '~src/Collections/utils/CollectionUtils';
import { isMetricsEnabled } from '~src/metrics/util/MetricUtils';
import { searchTelemetry } from '~src/search/components/SearchTelemetry';
import { ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';

import { BindUI } from '../common/UIBinding';
import { QuickLinksTestId } from '../components/QuickLinks';
import { IQuickLinksContentTypes, OmniBoxItemType } from '../models/Types';
import { SearchEPTService } from '../services/SearchEPTService';
import { SearchService } from '../services/SearchService';
import { IKeyboardNavigableItem, QuickSearchModule } from './QuickSearchModule';

const emptySearch = '?' + ContentFiltersKeys.search + '=';

export const ImpressionType = 'quickLinks';

export interface QuickLinkContentTypeItem extends IKeyboardNavigableItem {
    content: IQuickLinksContentTypes;
    url: string;
}

export interface QuickLinkOwnerItem extends IKeyboardNavigableItem {
    content: IUserInfo;
    url: string;
}

export class QuickLinksViewModel extends QuickSearchModule {
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public contentTypeItems: QuickLinkContentTypeItem[];
    @BindUI() public areOwnersLoaded = false;
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public ownerItems: QuickLinkOwnerItem[] = [];
    @BindUI() public currentKeyboardNavigableItem?: IKeyboardNavigableItem;

    public initialize(): void {
        const quickLinksContentTypeMap: Record<string, IQuickLinksContentTypes> = {
            [OmniBoxItemType.views]: {
                name: Messages.views(),
                getPath: (urls) => urls.searchViewsUrl() + emptySearch,
                icon: 'view_18px-large.svg',
            },
            [OmniBoxItemType.workbooks]: {
                name: Messages.workbooks(),
                getPath: (urls) => urls.searchWorkbooksUrl() + emptySearch,
                icon: 'workbook_18px-large.svg',
            },
            [OmniBoxItemType.metrics]: {
                name: Messages.metrics(),
                getPath: (urls) => urls.searchMetricsUrl() + emptySearch,
                icon: 'metrics_18px-large.svg',
            },
            [OmniBoxItemType.collections]: {
                name: Messages.collections(),
                getPath: (urls) => urls.searchCollectionsUrl() + emptySearch,
                icon: 'collection_18px-large.svg',
            },
            [OmniBoxItemType.datasources]: {
                name: Messages.datasources(),
                getPath: (urls) => urls.searchDatasourcesUrl() + emptySearch,
                icon: 'datasource_18px-large.svg',
            }
        };

        const contentTypes = Object.keys(quickLinksContentTypeMap).filter(contentType => !(contentType === OmniBoxItemType.metrics && !isMetricsEnabled()) && !(contentType === OmniBoxItemType.collections && !CollectionUtils.checkCollectionSiteSetting()));

        this.contentTypeItems = contentTypes.slice(0, 5).map((item, index) => {
            const content = quickLinksContentTypeMap[item];
            return {
                content: content,
                url: this.getContentTypeUrl(content),
                ownerModule: this,
                getIdentifier: () => QuickLinksTestId + '-ContentType-' + index.toString()

            };
        });
    }

    public onMount() {
        super.onMount();
        this.loadOwners();

        if (!this.isUnmounted && FeatureFlags.searchProductMetrics) {
            searchTelemetry.sendSearchImpressionEvent('', ImpressionType);
        }
    }

    public selectItemByKeyboard(): void {
        const contentTypeItem = this.currentKeyboardNavigableItem as QuickLinkContentTypeItem;
        const ownerItem = this.currentKeyboardNavigableItem as QuickLinkOwnerItem;

        if (this.contentTypeItems.indexOf(contentTypeItem) !== -1) {
            this.onContentTypeLinkClicked(contentTypeItem);
        } else {
            this.onOwnerItemClicked(ownerItem);
        }
    }

    public isKeyboardNavigable() {
        return !this.isUnmounted && (this.contentTypeItems?.length > 0 || this.ownerItems?.length > 0);
    }

    public onContentTypeLinkClicked(item: QuickLinkContentTypeItem) {
        this.omniboxViewModel.onResultItemSelected(OmniBoxItemType.contentTypeItem, this.contentTypeItems.length, this.contentTypeItems.indexOf(item), '-1', true);
        this.omniboxViewModel.closeSearch();
        if (FeatureFlags.searchEPT) {
            SearchEPTService.instance.start(EPTSessionType.fullSearch, '');
        }
        Location.instance.setUrl(item.url);
    }

    public onOwnerItemClicked(item: QuickLinkOwnerItem) {
        this.omniboxViewModel.onResultItemSelected(OmniBoxItemType.ownerItem, this.ownerItems.length, this.ownerItems.indexOf(item), '-1', true);
        this.omniboxViewModel.closeSearch();
        if (FeatureFlags.searchEPT) {
            SearchEPTService.instance.start(EPTSessionType.fullSearch, '');
        }
        Location.instance.setUrl(item.url);
    }

    public buildKeyboardNavigationPathInOwnModule() {
        this.contentTypeItems.forEach(item => {
            item.up = this.previousKeyboardNavigableModule.getLastKeyboardNavigableItem();
        });

        for (let i = 0; i < this.contentTypeItems.length - 1; i++) {
            const currentItem = this.contentTypeItems[i];
            const nextItem = this.contentTypeItems[i + 1];
            this.linkTwoKeyboardNavigableItems(currentItem, nextItem, false);
        }

        for (let i = 0; i < this.ownerItems.length - 1; i++) {
            const currentItem = this.ownerItems[i];
            const nextItem = this.ownerItems[i + 1];
            this.linkTwoKeyboardNavigableItems(currentItem, nextItem, false);
        }

        for (let i = 0; i < this.contentTypeItems.length; i++) {
            if (this.ownerItems.length > 0) {
                if (this.ownerItems[i]) {
                    this.contentTypeItems[i].down = this.ownerItems[i];
                } else {
                    this.contentTypeItems[i].down = this.ownerItems[this.ownerItems.length - 1];
                }
            } else {
                this.contentTypeItems[i].down = this.nextKeyboardNavigableModule.getFirstKeyboardNavigableItem();
            }
        }

        for (let i = 0; i < this.ownerItems.length; i++) {
            if (this.contentTypeItems.length > 0) {
                if (this.contentTypeItems[i]) {
                    this.ownerItems[i].up = this.contentTypeItems[i];
                } else {
                    this.ownerItems[i].up = this.contentTypeItems[this.contentTypeItems.length - 1];
                }
            } else {
                this.ownerItems[i].up = this.previousKeyboardNavigableModule.getLastKeyboardNavigableItem();
            }
        }

        this.ownerItems.forEach(item => {
            item.down = this.nextKeyboardNavigableModule.getFirstKeyboardNavigableItem();
        });
    }

    public getFirstKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.contentTypeItems[0];
    }

    public getLastKeyboardNavigableItem(): IKeyboardNavigableItem {
        if (this.ownerItems.length > 0) {
            return this.ownerItems[0];
        }
        return this.contentTypeItems[0];
    }

    public shouldShowOwnerList() {
        return !SearchService.instance.Server.isLimitedUserVisibilityEnforced();
    }

    public loadOwners() {
        this.areOwnersLoaded = false;
        this.ownerItems = [];

        if (FeatureFlags.searchQuickLinks && !SearchService.instance.Server.isLimitedUserVisibilityEnforced()) {
            SearchService.instance.recentUsers().then(result => {
                //
                //  This timeout is (unfortunately) required to have 2 renders:
                //  1. Render the removal of loading timer, to be replaced with the owner section (showPending = False)
                //  2. Render the owner section with results.
                //  This creates the conditions for the animation to occur because the height of that section
                //  goes from X pixels to 0, triggering the transition.
                //
                setTimeout(() => {
                    const owners = result.items.slice(0, 5);
                    SearchService.instance.usersWithProfilePictures(owners.map(u => u.id)).then(ownersWithProfilePicture => {
                        this.areOwnersLoaded = true;
                        this.ownerItems = owners.map((owner, index) => {
                            return {
                                content: ownersWithProfilePicture.items.find(ownerWithProfilePicture => owner.id === ownerWithProfilePicture.id),
                                url: this.getUserFilteredSearchUrl(owner),
                                ownerModule: this,
                                getIdentifier: () => QuickLinksTestId + '-Owner-' + index.toString()
                            };
                        });
                    });
                });
            });
        }
    }

    private getUserFilteredSearchUrl(user: IUserInfo): string {
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
        return (FeatureFlags.searchV2 ? urls.searchUrl() : urls.searchViewsUrl()) + '?' + ContentFiltersKeys.owner + '=' + user.id + '&' + ContentFiltersKeys.search + '=';
    }

    private getContentTypeUrl(content: IQuickLinksContentTypes): string {
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
        return content.getPath(urls);
    }
}